import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy } from 'react';

// Lazy load pages
const CustomersPage = lazy(() => import('@/app/customers/page'));
const CustomersUploadPage = lazy(() => import('@/app/customers/upload/page'));
const CustomerCheckPage = lazy(() => import('@/app/customer/check/page'));
const CustomerCreatePage = lazy(() => import('@/app/customer/create/page'));
const CustomerDetailsPage = lazy(() => import('@/app/customer/[id]/(tabs)/details/page'));
const CustomerIdentificationPage = lazy(() => import('@/app/customer/[id]/(tabs)/identification/page'));
const CustomerSessionsPage = lazy(() => import('@/app/customer/[id]/(tabs)/sessions/page'));
const CustomerSessionEditPage = lazy(() => import('@/app/customer/[id]/(tabs)/sessions/[sessionId]/edit/page'));
const CustomerSessionResultPage = lazy(() => import('@/app/customer/[id]/(tabs)/sessions/[sessionId]/result/[resultId]/page'));
const CustomerMinorPage = lazy(() => import('@/app/customer/[id]/minor/page'));
const GuardianCreatePage = lazy(() => import('@/app/guardian/create/page'));
const SessionsPage = lazy(() => import('@/app/sessions/page'));
const SessionPage = lazy(() => import('@/app/session/[id]/page'));
const OrganisationCheckPage = lazy(() => import('@/app/organisation/check/page'));
const OrganisationErrorPage = lazy(() => import('@/app/organisation/error/page'));
const Error401Page = lazy(() => import('@/app/(errors)/401/page'));
const Error403Page = lazy(() => import('@/app/(errors)/403/page'));
const NotFoundPage = lazy(() => import('@/app/not-found'));

// Import layouts
import CustomerTabsLayout from '@/app/customer/[id]/(tabs)/layout';
import AppLayout from '@/app/app-layout';
import { useValidatedParams, Param } from './useValidatedParams';
import { Alert } from '@/components';

// Wrapper components for route params
type PageWithParamsProps<T extends readonly Param[]> = {
  params: {
    [K in T[number]]: string;
  };
};

function ValidatedRoute<T extends readonly Param[]>({
  Component,
  paramsToValidate,
}: Readonly<{ Component: React.ComponentType<PageWithParamsProps<T>>; paramsToValidate: T }>) {
  const validatedParams = useValidatedParams(...paramsToValidate);

  if (validatedParams === undefined) {
    return (
      <Alert type="error" title="Cannot load this page">
        <p>The page could not be loaded. Please check the link and try again.</p>
      </Alert>
    );
  }

  return <Component params={validatedParams} />;
}

export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <AppLayout />,
      children: [
        {
          index: true,
          element: <Navigate to="/customers" replace />,
        },
        {
          path: 'customers',
          element: <CustomersPage />,
        },
        {
          path: 'customers/upload',
          element: <CustomersUploadPage />,
        },
        {
          path: 'customer/check',
          element: <CustomerCheckPage />,
        },
        {
          path: 'customer/create',
          element: <CustomerCreatePage />,
        },
        {
          path: 'customer/:customerId',
          element: <ValidatedRoute Component={CustomerTabsLayout} paramsToValidate={['customerId']} />,
          children: [
            {
              index: true,
              element: <Navigate to="details" replace />,
            },
            {
              path: 'details',
              element: <ValidatedRoute Component={CustomerDetailsPage} paramsToValidate={['customerId']} />,
            },
            {
              path: 'identification',
              element: <ValidatedRoute Component={CustomerIdentificationPage} paramsToValidate={['customerId']} />,
            },
            {
              path: 'sessions',
              element: <ValidatedRoute Component={CustomerSessionsPage} paramsToValidate={['customerId']} />,
            },
            {
              path: 'sessions/:sessionId/edit',
              element: <ValidatedRoute Component={CustomerSessionEditPage} paramsToValidate={['customerId', 'sessionId']} />,
            },
            {
              path: 'sessions/:sessionId/result/:resultId',
              element: <ValidatedRoute Component={CustomerSessionResultPage} paramsToValidate={['customerId', 'sessionId', 'resultId']} />,
            },
          ],
        },
        {
          path: 'customer/:customerId/minor',
          element: <ValidatedRoute Component={CustomerMinorPage} paramsToValidate={['customerId']} />,
        },
        {
          path: 'guardian/create',
          element: <GuardianCreatePage />,
        },
        {
          path: 'sessions',
          element: <SessionsPage />,
        },
        {
          path: 'session/:sessionId',
          element: <ValidatedRoute Component={SessionPage} paramsToValidate={['sessionId']} />,
        },
        {
          path: 'organisation/check',
          element: <OrganisationCheckPage />,
        },
        {
          path: 'organisation/error',
          element: <OrganisationErrorPage />,
        },
        {
          path: '401',
          element: <Error401Page />,
        },
        {
          path: '403',
          element: <Error403Page />,
        },
        {
          path: '*',
          element: <NotFoundPage />,
        },
      ],
    },
  ],
  {
    basename: '/enrolments',
  },
);
